import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ChevronLeft, Shield, Mail, MapPin, Phone, FileText } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import { useLanguage } from "@/context/LanguageContext";

const COPY = {
  tr: {
    home: "Ana Sayfa",
    kvkkBadge: "6698 sayılı Kanun uyumlu",
    title: "Kişisel Verilerin Korunması (KVKK) Aydınlatma Metni",
    lastUpdated: "Son güncelleme",
    lang: "tr",
    intro: (name, type) => (<>
      <strong>{name}</strong> ({type}) — bu web sitesini işleten <strong>KAHU</strong> platformunun sahibi ve veri sorumlusu — kişisel verilerinizin 6698 sayılı Kişisel Verilerin Korunması Kanunu (&ldquo;KVKK&rdquo;) ve ilgili mevzuata uygun olarak işlenmesini önemsemektedir. Bu aydınlatma metni, KVKK m. 10 kapsamındaki aydınlatma yükümlülüğünü yerine getirmek üzere hazırlanmıştır.
    </>),
    s1: {
      title: "1. Veri Sorumlusunun Kimliği",
      inst: "Kurum:",
      merisLabel: "MERSİS/VKN:",
      verbisLabel: "VERBİS Kayıt Durumu:",
    },
    s2: {
      title: "2. İşlenen Kişisel Veri Kategorileri",
      lead: "KAHU platformu üzerinden aşağıdaki veri kategorileri işlenmektedir:",
      items: [
        ["Kimlik verileri:", "Ad-soyad, doğum yılı (yaş beyanı) — sahiplendirme başvurusu ve gönüllü kaydı için."],
        ["İletişim verileri:", "E-posta, telefon, adres, şehir/ilçe — başvuru dönüşü, koruyucu aile iletişimi, bağışçı teşekkür yazışması."],
        ["Yaşam alanı ve deneyim bilgileri:", "Ev tipi, çocuk/başka hayvan bilgisi, hayvan bakım deneyimi — sahiplendirme uygunluk değerlendirmesi."],
        ["Finansal veriler:", "Bağış tutarı, ödeme tarihi, ödeme yöntemi (kart bilgisi bizde tutulmaz; Iyzico/Fonzip PCI-DSS ortamında saklar)."],
        ["İşlem güvenlik verileri:", "IP adresi, tarayıcı bilgisi, oturum çerezleri — güvenlik ve trafik analizi."],
        ["Görsel içerik:", "Koruyucu aile ya da sahiplendirilen can güncelleme fotoğrafları (isteğe bağlı, açık rızaya dayalı)."],
      ],
    },
    s3: { title: "3. İşleme Amaçları", items: [
      "Sahiplendirme başvurularının değerlendirilmesi ve süreç yönetimi.",
      "Koruyucu aile (bağışçı) kaydı, düzenli bağışçı iletişimi ve teşekkür yazışmaları.",
      "Bağış işlemlerinin gerçekleştirilmesi, muhasebe/vergi kayıtlarının tutulması.",
      "Sahiplendirilen canların yaşam kanıtı (30/90/180 gün) takibi ve şeffaf raporlama.",
      "Web sitesi trafiği analizi, güvenlik ve hizmet iyileştirmesi.",
      "Yasal yükümlülükler kapsamında ilgili kurumlara bildirim (Vergi, Dernekler Müdürlüğü, KVKK Kurulu vb.).",
    ]},
    s4: { title: "4. Hukuki Sebepler", lead: "Kişisel verileriniz KVKK m. 5 ve m. 6 kapsamındaki aşağıdaki hukuki sebeplerle işlenmektedir:", items: [
      ["Sözleşmenin kurulması ve ifası", "(m. 5/2-c): sahiplendirme başvurusu, koruyucu aile taahhüdü."],
      ["Hukuki yükümlülük", "(m. 5/2-ç): dernek mevzuatı, vergi ve muhasebe zorunlulukları."],
      ["Meşru menfaat", "(m. 5/2-f): web sitesi analitiği, hizmet güvenliği, dolandırıcılık önleme."],
      ["Açık rıza", "(m. 5/1 ve m. 6/2): bülten aboneliği, koruyucu aile foto paylaşımı gibi opsiyonel kullanımlar."],
    ]},
    s5: { title: "5. Kişisel Verilerin Aktarımı", lead: "Verileriniz, işleme amaçlarıyla sınırlı olarak aşağıdaki taraflara aktarılabilir:", items: [
      ["Ödeme kuruluşları:", "Iyzico (Iyzi Ödeme ve Elektronik Para Hizmetleri A.Ş.), Fonzip — bağış işlemlerinin gerçekleştirilmesi için."],
      ["E-posta hizmet sağlayıcıları:", "Google Workspace SMTP relay ve Resend (yedek) — bilgilendirme e-postaları için."],
      ["Barındırma ve altyapı:", "MongoDB Atlas ve Kubernetes tabanlı bulut altyapısı — verilerin güvenli saklanması için."],
      ["Kamu kurumları:", "Yasal talep halinde Dernekler Müdürlüğü, Vergi Daireleri, KVKK Kurulu, mahkemeler."],
      ["Belediye ortakları:", "Sadece belediye pilot programı kapsamında ve sahiplendirilen canlara özgü aggregate raporlar (kişisel veri anonimleştirilir)."],
    ], note: "Yurt dışına aktarım söz konusu olduğunda (örn. bulut altyapısı) KVKK m. 9 gereğince yeterli koruma önlemleri alınır veya ayrıca açık rızanız istenir." },
    s6: { title: "6. KVKK m. 11 Hakları", lead: "İlgili kişi sıfatıyla aşağıdaki haklara sahipsiniz:", items: [
      "Kişisel verinizin işlenip işlenmediğini öğrenme,",
      "İşlenmişse buna ilişkin bilgi talep etme,",
      "İşleme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme,",
      "Yurt içi/dışı aktarılan üçüncü kişileri öğrenme,",
      "Eksik/yanlış işlenmişse düzeltilmesini isteme,",
      "KVKK m. 7 uyarınca silinmesini/yok edilmesini isteme,",
      "Düzeltme/silme işleminin aktarılan üçüncü kişilere bildirilmesini isteme,",
      "İşleme sonucu aleyhinize sonuç doğarsa buna itiraz etme,",
      "KVKK'ya aykırı işleme nedeniyle zarara uğrarsanız tazminat talep etme.",
    ]},
    s7: { title: "7. Başvuru Yöntemi", lead: "Yukarıdaki haklarınızı kullanmak için başvurunuzu:", emailLabel: "E-posta:", writtenLabel: "Yazılı olarak:", notary: "Veri Sorumlusuna Başvuru Usul ve Esasları Hakkında Tebliğ'e uygun şekilde noter aracılığıyla", note: (<>yollarından birini kullanarak yapabilirsiniz. Başvurunuz <strong>30 gün</strong> içinde sonuçlandırılır; başvuru ücretsizdir. Talebinizin reddi, eksik yanıt veya süresi içinde yanıt alamamanız halinde KVKK Kurulu&apos;na şikayet hakkınız saklıdır.</>) },
    s8: { title: "8. Çerez Kullanımı", body: "KAHU, yalnızca hizmetin işleyişi için gerekli teknik çerezleri (oturum kimliği, dil tercihi, güvenlik) kullanır. Analitik/pazarlama çerezleri şu an aktif değildir; ileride Google Analytics (GA4) veya Meta Pixel entegrasyonu yapılırsa bu metin güncellenir ve gerekli hallerde açık rızanız ayrıca istenir." },
    s9: { title: "9. Saklama Süreleri", items: [
      ["Sahiplendirme başvuruları:", "Yaşam kanıtı takibi tamamlanana kadar aktif; sonrasında en fazla 5 yıl."],
      ["Koruyucu aile kayıtları:", "Bağış aktif olduğu sürece + iptal sonrası muhasebe için 5 yıl."],
      ["Bağış işlem kayıtları:", "Vergi mevzuatı gereği en az 10 yıl."],
      ["İletişim formu verileri:", "İşlem tamamlandıktan sonra 2 yıl."],
    ]},
    s10: { title: "10. Güncellemeler", body: (lu) => (<>Bu aydınlatma metni mevzuat değişiklikleri veya faaliyet kapsamında güncellemelere bağlı olarak revize edilebilir. Güncel sürüm her zaman bu sayfada yayımlanır. Son güncelleme tarihi: <strong>{lu}</strong>.</>) },
    footerDisclaimer: "Bu metin bilgi amaçlıdır. Hukuki danışmanlık için lütfen bir avukata başvurun.",
    privacyLink: "Gizlilik Politikası",
  },
  en: {
    home: "Home",
    kvkkBadge: "Compliant with Law No. 6698 (KVKK)",
    title: "Personal Data Protection (KVKK) Disclosure",
    lastUpdated: "Last updated",
    lang: "en",
    intro: (name, type) => (<>
      <strong>{name}</strong> ({type}) — owner and data controller of the <strong>KAHU</strong> platform that operates this website — takes the processing of your personal data seriously and in accordance with Turkey&apos;s Law No. 6698 on the Protection of Personal Data (&ldquo;KVKK&rdquo;) and related regulations. This disclosure has been prepared to fulfil the information obligation under Article 10 of the KVKK.
    </>),
    s1: {
      title: "1. Identity of the Data Controller",
      inst: "Institution:",
      merisLabel: "MERSIS / Tax No:",
      verbisLabel: "VERBIS Registration Status:",
    },
    s2: {
      title: "2. Categories of Personal Data Processed",
      lead: "The following categories of data are processed via the KAHU platform:",
      items: [
        ["Identity data:", "Full name, year of birth (age declaration) — for adoption applications and volunteer registration."],
        ["Contact data:", "Email, phone, address, city/district — for reaching back after applications, communication with Guardian Families, donor acknowledgements."],
        ["Living environment & experience info:", "Home type, information about children / other animals, animal care experience — for adoption eligibility assessment."],
        ["Financial data:", "Donation amount, payment date, payment method (card details are NOT stored by us; Iyzico/Fonzip store them in their PCI-DSS environment)."],
        ["Transaction security data:", "IP address, browser info, session cookies — for security and traffic analysis."],
        ["Visual content:", "Update photos from Guardian Families or adopted companions (optional, based on explicit consent)."],
      ],
    },
    s3: { title: "3. Purposes of Processing", items: [
      "Evaluating adoption applications and managing the process.",
      "Registration of Guardian Families (donors), ongoing donor communication and thank-you correspondence.",
      "Executing donation transactions and keeping accounting / tax records.",
      "Follow-up of life-proof (30/90/180 days) for adopted companions and transparent reporting.",
      "Website traffic analysis, security and service improvement.",
      "Notification to relevant authorities under legal obligations (Tax Authority, Directorate of Associations, KVKK Board, etc.).",
    ]},
    s4: { title: "4. Legal Grounds", lead: "Your personal data is processed under the following legal grounds within Articles 5 and 6 of the KVKK:", items: [
      ["Establishment and performance of a contract", "(Art. 5/2-c): adoption application, Guardian Family commitment."],
      ["Legal obligation", "(Art. 5/2-ç): association regulations, tax and accounting obligations."],
      ["Legitimate interest", "(Art. 5/2-f): website analytics, service security, fraud prevention."],
      ["Explicit consent", "(Art. 5/1 and Art. 6/2): optional uses such as newsletter subscription or Guardian Family photo sharing."],
    ]},
    s5: { title: "5. Transfer of Personal Data", lead: "Your data may be shared with the following parties, limited to the purposes of processing:", items: [
      ["Payment institutions:", "Iyzico (Iyzi Payment and Electronic Money Services Inc.), Fonzip — for executing donation transactions."],
      ["Email service providers:", "Google Workspace SMTP relay and Resend (backup) — for informational emails."],
      ["Hosting & infrastructure:", "MongoDB Atlas and Kubernetes-based cloud infrastructure — for secure storage of data."],
      ["Public authorities:", "Upon legal request: Directorate of Associations, Tax Offices, KVKK Board, courts."],
      ["Municipality partners:", "Only within the scope of the municipality pilot programme and only aggregate reports specific to adopted companions (personal data is anonymised)."],
    ], note: "When cross-border transfer occurs (e.g. cloud infrastructure), adequate safeguards are taken under Article 9 of the KVKK, or your explicit consent is separately requested." },
    s6: { title: "6. Your Rights Under Article 11 of the KVKK", lead: "As the relevant person, you have the following rights:", items: [
      "learn whether your personal data is being processed,",
      "if so, request information about the processing,",
      "learn the purpose of processing and whether it is used appropriately,",
      "learn the third parties to whom the data is transferred, domestic or abroad,",
      "request correction if the data is incomplete or inaccurate,",
      "request deletion / destruction under Article 7 of the KVKK,",
      "request that the correction/deletion be notified to third parties to whom the data was transferred,",
      "object if the processing produces a result against you,",
      "claim compensation for damages arising from processing contrary to the KVKK.",
    ]},
    s7: { title: "7. How to Apply", lead: "To exercise the rights above, you can submit your application via:", emailLabel: "Email:", writtenLabel: "In writing:", notary: "Through a notary in accordance with the Communiqué on Application to the Data Controller.", note: (<>one of these channels. Your application will be concluded within <strong>30 days</strong>; the application is free of charge. If your request is rejected, the answer is insufficient, or no response is received within the deadline, your right to complain to the KVKK Board is reserved.</>) },
    s8: { title: "8. Use of Cookies", body: "KAHU only uses the technical cookies necessary for the operation of the service (session identifier, language preference, security). Analytics / marketing cookies are not currently active; if Google Analytics (GA4) or Meta Pixel integrations are added in the future, this text will be updated and your explicit consent will be separately requested where required." },
    s9: { title: "9. Retention Periods", items: [
      ["Adoption applications:", "Active until life-proof follow-up is completed; then up to 5 years."],
      ["Guardian Family records:", "While the donation is active + 5 years for accounting after cancellation."],
      ["Donation transaction records:", "At least 10 years as required by tax legislation."],
      ["Contact form data:", "2 years after the request is completed."],
    ]},
    s10: { title: "10. Updates", body: (lu) => (<>This disclosure may be revised due to changes in legislation or the scope of activities. The current version is always published on this page. Last updated: <strong>{lu}</strong>.</>) },
    footerDisclaimer: "This text is for information only. For legal advice, please consult a lawyer.",
    privacyLink: "Privacy Policy",
  },
};

export default function KVKKPage() {
  const [legal, setLegal] = useState({});
  const [loading, setLoading] = useState(true);
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.tr;

  useEffect(() => {
    axios.get(`${API}/content?category=legal`)
      .then(r => { setLegal(r.data || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const g = (key, fallback = "") => legal[key] || fallback;

  const entityName = g("legal.entity_name", "Pativista");
  const entityType = g("legal.entity_type", locale === "en" ? "Association" : "Dernek");
  const address = g("legal.address");
  const contactEmail = g("legal.contact_email", "patibekcileri@gmail.com");
  const contactPhone = g("legal.contact_phone");
  const merisNo = g("legal.meris_no");
  const verbisStatus = g("legal.verbis_status");
  const lastUpdated = g("legal.last_updated", "2026-07-01");

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="kvkk-back-btn">
          <ChevronLeft className="w-4 h-4" /> {c.home}
        </Link>

        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-3">
            <Shield className="w-3 h-3" /> {c.kvkkBadge}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl mb-2" data-testid="kvkk-title">{c.title}</h1>
          <p className="text-sm text-muted-foreground">
            {c.lastUpdated}: <span data-testid="kvkk-last-updated">{lastUpdated}</span>
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
        )}

        {!loading && (
          <article
            lang={c.lang}
            className="prose prose-neutral max-w-none space-y-8 text-foreground kvkk-article"
            style={{ hyphens: "none", wordBreak: "normal", overflowWrap: "break-word" }}
          >
            <section>
              <p className="text-base leading-relaxed">{c.intro(entityName, entityType)}</p>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s1.title}</h2>
              <ul className="space-y-1.5 text-sm">
                <li><strong>{c.s1.inst}</strong> {entityName} ({entityType})</li>
                {address && <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" /> {address}</li>}
                {contactEmail && <li className="flex items-center gap-2"><Mail className="w-4 h-4 flex-shrink-0 text-muted-foreground" /> <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">{contactEmail}</a></li>}
                {contactPhone && <li className="flex items-center gap-2"><Phone className="w-4 h-4 flex-shrink-0 text-muted-foreground" /> {contactPhone}</li>}
                {merisNo && <li><strong>{c.s1.merisLabel}</strong> {merisNo}</li>}
              </ul>
              {verbisStatus && (
                <p className="mt-3 text-sm text-muted-foreground italic bg-muted/40 p-3 rounded-lg">
                  <strong className="text-foreground not-italic">{c.s1.verbisLabel}</strong> {verbisStatus}
                </p>
              )}
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s2.title}</h2>
              <p className="text-sm mb-3">{c.s2.lead}</p>
              <ul className="space-y-1.5 text-sm list-disc pl-6 space-y-2">
                {c.s2.items.map(([bold, rest], i) => (
                  <li key={i}><strong>{bold}</strong> {rest}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s3.title}</h2>
              <ul className="space-y-1.5 text-sm list-disc pl-6 space-y-2">
                {c.s3.items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s4.title}</h2>
              <p className="text-sm mb-3">{c.s4.lead}</p>
              <ul className="space-y-1.5 text-sm list-disc pl-6 space-y-2">
                {c.s4.items.map(([bold, rest], i) => (
                  <li key={i}><strong>{bold}</strong> {rest}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s5.title}</h2>
              <p className="text-sm mb-3">{c.s5.lead}</p>
              <ul className="space-y-1.5 text-sm list-disc pl-6 space-y-2">
                {c.s5.items.map(([bold, rest], i) => (
                  <li key={i}><strong>{bold}</strong> {rest}</li>
                ))}
              </ul>
              <p className="text-sm mt-3 text-muted-foreground">{c.s5.note}</p>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s6.title}</h2>
              <p className="text-sm mb-3">{c.s6.lead}</p>
              <ul className="space-y-1.5 text-sm list-disc pl-6 space-y-2">
                {c.s6.items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s7.title}</h2>
              <p className="text-sm">{c.s7.lead}</p>
              <ul className="space-y-1.5 text-sm list-disc pl-6 space-y-2 mt-2">
                <li>{contactEmail && <>{c.s7.emailLabel} <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">{contactEmail}</a></>}</li>
                {address && <li>{c.s7.writtenLabel} {address}</li>}
                <li>{c.s7.notary}</li>
              </ul>
              <p className="text-sm mt-3">{c.s7.note}</p>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s8.title}</h2>
              <p className="text-sm leading-relaxed">{c.s8.body}</p>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s9.title}</h2>
              <ul className="space-y-1.5 text-sm list-disc pl-6 space-y-2">
                {c.s9.items.map(([bold, rest], i) => (
                  <li key={i}><strong>{bold}</strong> {rest}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl mb-3">{c.s10.title}</h2>
              <p className="text-sm">{c.s10.body(lastUpdated)}</p>
            </section>

            <div className="border-t border-border/50 pt-6 mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">{c.footerDisclaimer}</p>
              <Link to="/gizlilik" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="kvkk-privacy-link">
                <FileText className="w-4 h-4" /> {c.privacyLink}
              </Link>
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
